import { parseWebsiteScanList } from './website-scan-list';
import { stripWebsiteReportSummary } from './website-report-summary';
import { parseWebsiteReportReference, withoutServerContext } from './website-report-reference';

export interface AttachmentMarkerInput {
  id: string;
  filename: string;
}

export interface ParsedAttachmentRef {
  id: string;
  filename: string;
}

export interface ParsedMessageContent {
  text: string;
  attachmentRefs: ParsedAttachmentRef[];
}

const ATTACHMENT_MARKER_REGEX =
  /!\[([^\]]*)\]\(apex-attachment:\/\/([0-9a-f-]{36})\)/gi;

// The backend (apex-platform-backend `buildAttachmentContext`) appends an LLM-only block
// of image/PDF descriptions to the stored message content so the text-only agent can "see"
// the attachments on later turns. It is always appended last and led by this exact phrase.
// It must NOT be shown in the user's message bubble — strip everything from here onward.
const ATTACHMENT_CONTEXT_LEADIN =
  'The user attached files. Use this extracted attachment context when relevant:';

// The Nova AI browser extension (apex-browser-extension `page-context.ts`) appends a
// machine-only block to the user's message telling the agent which page is being viewed
// and how the capture-marker protocol works. Like the attachment context, it is always
// appended last and led by this exact phrase — strip from it onward. Must stay in sync
// with `PAGE_CONTEXT_LEADIN` in the extension.
const PAGE_CONTEXT_LEADIN =
  'Browser context — the page this user is currently viewing:';

// The backend (apex-platform-backend `buildPageContext`) PREPENDS an LLM-only block naming
// the report the user is on, because Abbit never puts `conversation.attributes` in the
// model's context — the agent was answering "I couldn't retrieve the report type from the
// current context" on conversations that stored `reportType: "website"`. It is always the
// first thing in the stored content and must NOT be shown in the user's message bubble.
const PAGE_CONTEXT_OPEN = '[APEX SYSTEM CONTEXT]';
const PAGE_CONTEXT_CLOSE = '[END APEX SYSTEM CONTEXT]';

export function buildMarkers(attachments: AttachmentMarkerInput[]): string {
  return attachments
    .map(
      (attachment) =>
        `![${sanitizeMarkerFilename(attachment.filename)}](apex-attachment://${
          attachment.id
        })`
    )
    .join('\n');
}

export function parseMessageContent(rawContent: string): ParsedMessageContent {
  const withoutPageContext = stripWebsiteReportSummary(stripPageContext(rawContent));
  const content = parseWebsiteScanList(withoutPageContext)?.text ?? withoutPageContext;
  const attachmentRefs: ParsedAttachmentRef[] = [];

  for (const match of content.matchAll(ATTACHMENT_MARKER_REGEX)) {
    attachmentRefs.push({
      filename: match[1] || 'Attachment',
      id: match[2],
    });
  }

  let text = content.replace(ATTACHMENT_MARKER_REGEX, '');

  // Drop the appended LLM-only blocks (attachment context, extension page context) so
  // they don't clutter the bubble. Each is appended after the user's own text, so cutting
  // at the earliest lead-in also removes anything appended after it.
  for (const leadIn of [ATTACHMENT_CONTEXT_LEADIN, PAGE_CONTEXT_LEADIN]) {
    const contextIndex = text.indexOf(leadIn);
    if (contextIndex !== -1) {
      text = text.slice(0, contextIndex);
    }
  }

  text = text.replace(/[ \t]+\n/g, '\n').trim();

  return {
    text,
    attachmentRefs,
  };
}

/**
 * Drop the prepended page-context block. Anchored at the very start, because that is the
 * only place the backend writes it — a user who types the delimiter mid-message keeps
 * their own text intact.
 *
 * Exported because the seeded scan-analysis turn is posted through the same send path and
 * so carries the block too: the `startsWith(...)` checks that hide those technical turns
 * from the thread have to strip it before they compare.
 */
export function stripPageContext(content: string): string {
  const text = withoutServerContext(content);
  return parseWebsiteReportReference(text)?.text ?? text;
}

function sanitizeMarkerFilename(filename: string): string {
  const sanitized = filename.replace(/[\]\r\n]+/g, ' ').trim();
  return sanitized || 'Attachment';
}
